// API para calendarios compartidos — ADR-2026-10-15-calendario-compartido.
//
// El calendario se comparte con un token aleatorio. Los visitantes sin cuenta
// acceden a funciones SECURITY DEFINER que validan el token internamente.
// Las funciones de lectura usan el supabase client con la anon key — no
// necesitan sesión porque las funciones SQL están `grant to anon`.

import { supabase } from '../../../lib/supabase';
import { esNombreDeVideo, nombreDelPoster } from '../../../utils/posterDeVideo';
import type { EstadoDePost, MedioDePost } from './post';

// ── Tipos ──────────────────────────────────────────────────────────────────

export interface InfoCalendarioCompartido {
  lanzamientoId: string;
  idCompany: string;
  nombreMarca: string;
  logoUrl: string | null;
}

export interface PostCompartido {
  id: string;
  texto: string;
  cuando: Date | null;
  estado: EstadoDePost;
  medios: MedioDePost[];
  miniaturas: string[];
}

export interface ComentarioDeVisitante {
  id: string;
  nombreVisitante: string;
  texto: string;
  resuelto: boolean;
  createdAt: string;
  esVisitante: boolean;
}

// ── Generar token (autenticado) ────────────────────────────────────────────

function generarToken(): string {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  // base64url sin padding
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/** Crea (o devuelve el existente) un link para compartir el calendario. */
export async function crearLinkCompartido(
  lanzamientoId: string,
  companyId: string,
): Promise<{ token: string | null; error: string | null }> {
  // ¿Ya hay uno activo?
  const { data: existente } = await supabase
    .from('calendario_compartido')
    .select('token')
    .eq('lanzamiento_id', lanzamientoId)
    .eq('id_company', companyId)
    .is('revocado_at', null)
    .limit(1)
    .single();

  if (existente?.token) {
    return { token: existente.token, error: null };
  }

  const token = generarToken();
  const { error } = await supabase.from('calendario_compartido').insert({
    token,
    lanzamiento_id: lanzamientoId,
    id_company: companyId,
  });

  if (error) {
    console.error('[compartirApi] crear link', error);
    return { token: null, error: 'No se pudo crear el link' };
  }
  return { token, error: null };
}

/** Revoca el link activo. */
export async function revocarLink(
  lanzamientoId: string,
  companyId: string,
): Promise<{ error: string | null }> {
  const { error } = await supabase
    .from('calendario_compartido')
    .update({ revocado_at: new Date().toISOString() })
    .eq('lanzamiento_id', lanzamientoId)
    .eq('id_company', companyId)
    .is('revocado_at', null);

  if (error) {
    console.error('[compartirApi] revocar', error);
    return { error: 'No se pudo revocar el link' };
  }
  return { error: null };
}

// ── Lectura pública (sin sesión) ───────────────────────────────────────────

/** Valida el token y devuelve info de la marca. */
export async function infoDelCalendario(
  token: string,
): Promise<InfoCalendarioCompartido | null> {
  const { data, error } = await supabase.rpc('info_calendario_compartido', {
    p_token: token,
  });
  if (error || !data || (Array.isArray(data) && data.length === 0)) {
    console.error('[compartirApi] info', error);
    return null;
  }
  const r = Array.isArray(data) ? data[0] : data;
  return {
    lanzamientoId: r.lanzamiento_id,
    idCompany: r.id_company,
    nombreMarca: r.nombre_marca,
    logoUrl: r.logo_url ?? null,
  };
}

/** Los posts del calendario compartido. */
export async function fetchPostsCompartidos(
  token: string,
): Promise<PostCompartido[]> {
  const { data, error } = await supabase.rpc('posts_del_calendario_compartido', {
    p_token: token,
  });
  if (error || !data) {
    console.error('[compartirApi] posts', error);
    return [];
  }

  // Pedir medios de TODOS los posts en paralelo (no secuencial).
  const posts = await Promise.all(
    (data as any[]).map(async (p) => {
      const idCompany = p.id_company as string;
      const { data: mediosData } = await supabase.rpc('medios_del_post_compartido', {
        p_token: token, p_post_id: p.id,
      });

      const mediosCrudos = ((mediosData || []) as any[])
        .sort((a: any, b: any) => (a.orden ?? 0) - (b.orden ?? 0));

      const medios: MedioDePost[] = mediosCrudos.map((m: any, i: number) => {
        const fileName = m.file_name || m.gen_file_name || '';
        return {
          id: m.id, generadaId: m.imagen_generada_id, fileName,
          esVideo: esNombreDeVideo(fileName), orden: i,
        };
      });

      const miniaturas = await Promise.all(
        medios.map(async (m) => {
          if (!m.fileName) return '';
          if (m.esVideo) return firmarPublico(idCompany, nombreDelPoster(m.fileName), 'opt');
          return (await firmarPublico(idCompany, m.fileName, 'opt'))
            || (await firmarPublico(idCompany, m.fileName, 'full'));
        }),
      );

      return {
        id: p.id as string,
        texto: (p.descripcion as string) ?? '',
        cuando: p.programado_at ? new Date(p.programado_at as string) : null,
        estado: (p.estado as EstadoDePost) ?? 'pendiente',
        medios,
        miniaturas,
      };
    }),
  );

  return posts;
}

const BUCKET = 'generadas';
const SIGNED_URL_TTL = 3600;

async function firmarPublico(
  companyId: string,
  fileName: string,
  carpeta: 'opt' | 'full',
): Promise<string> {
  const { data } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(`${companyId}/${carpeta}/${fileName}`, SIGNED_URL_TTL);
  return data?.signedUrl || '';
}

// ── Comentarios de visitante ───────────────────────────────────────────────

export async function fetchComentariosDeVisitante(
  token: string,
  postId: string,
): Promise<ComentarioDeVisitante[]> {
  const { data, error } = await supabase.rpc('comentarios_de_visitante_del_post', {
    p_token: token,
    p_post_id: postId,
  });
  if (error || !data) {
    console.error('[compartirApi] comentarios', error);
    return [];
  }
  return (data as any[]).map((c) => ({
    id: c.id,
    nombreVisitante: c.nombre_visitante,
    texto: c.texto,
    resuelto: c.resuelto === true,
    createdAt: c.created_at,
    esVisitante: c.es_visitante === true,
  }));
}

export async function comentarComoVisitante(
  token: string,
  postId: string,
  nombre: string,
  texto: string,
): Promise<{ error: string | null }> {
  const { error } = await supabase.rpc('comentar_como_visitante', {
    p_token: token,
    p_post_id: postId,
    p_nombre: nombre.trim(),
    p_texto: texto.trim(),
  });
  if (error) {
    console.error('[compartirApi] comentar', error);
    return { error: 'No se pudo dejar el comentario' };
  }
  return { error: null };
}

/** Arma la URL completa del link compartido. */
export function urlDeCalendarioCompartido(token: string): string {
  return `${window.location.origin}/calendario/${token}`;
}
